import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';

import {
  endHalftime,
  movePlayer,
  startHalftime,
  updateAttendance,
  updateCard,
  tickRedCards,
  updateGoalDelta,
  updateAssistDelta,
  updateCardCorrect,
  setMatchEvents,
} from '../../store/slices/gameSlice';

import Button from '../Button/Button';
import { useAlertDialog } from '../ConfirmDialog/ConfirmDialogProvider';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import profilePic from '../../assets/images/profile_image.jpg';
import { buildFinishGamePayload } from './gameStatsPayload';
import {
  attachAssistToGoal,
  createCardEvent,
  createGoalEvent,
  createTeamSwitchEvents,
  describeMatchEvent,
  removeAssistFromLatestGoal,
  removeLastCardEvent,
  removeLastGoalEvent,
  sortMatchEvents,
} from './matchEvents';
import './GameStats.css';

function clampDropIndex(team, requestedIndex) {
  const safeTeamLength = Array.isArray(team) ? team.length : 0;
  if (safeTeamLength <= 0) {
    return 0;
  }

  const normalizedIndex = Number(requestedIndex);
  if (!Number.isInteger(normalizedIndex) || normalizedIndex < 0) {
    return 0;
  }

  return Math.min(normalizedIndex, safeTeamLength - 1);
}

const GameStats = ({ onFinish, runAuthedRequest, onAuthFailure }) => {
  const dispatch = useDispatch();
  const showAlert = useAlertDialog();
  const [isCorrectMode, setIsCorrectMode] = React.useState(false);
  const [pendingGoalSortIndex, setPendingGoalSortIndex] = React.useState(null);
  const [isSubmittingFinish, setIsSubmittingFinish] = React.useState(false);
  const {
    game,
    goals,
    assists,
    timer,
    halftimeStartedAt,
    halftimeCompleted,
    score,
    attendance,
    cards,
    redCardUntil,
    cardCounts,
    matchEvents,
  } = useSelector(
    (state) => state.game
  );

  // We only need "late" icons in phase 3 for absent players
  const LATE_ICON = {
    active: `${process.env.PUBLIC_URL}/icons/late_active.svg`,
    inactive: `${process.env.PUBLIC_URL}/icons/late_inactive.svg`,
  };
  const CARD_ICON = {
    none: `${process.env.PUBLIC_URL}/icons/no_card.svg`,
    yellow: `${process.env.PUBLIC_URL}/icons/yellow_card.svg`,
    red: `${process.env.PUBLIC_URL}/icons/red_card.svg`,
  };
  const isHalftime = Boolean(halftimeStartedAt && !halftimeCompleted);
  const actionButtonText = isHalftime
    ? 'RESTART'
    : halftimeCompleted
      ? isSubmittingFinish
        ? 'FINISHING...'
        : 'FINISH'
      : 'BREAK';

  React.useEffect(() => {
    const id = setInterval(() => {
      dispatch(tickRedCards(Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [dispatch]);

  React.useEffect(() => {
    if (isCorrectMode) {
      setPendingGoalSortIndex(null);
    }
  }, [isCorrectMode]);

  const pendingGoal = React.useMemo(
    () =>
      Array.isArray(matchEvents)
        ? matchEvents.find(
            (event) => event?.type === 'goal' && event.sortIndex === pendingGoalSortIndex
          ) || null
        : null,
    [matchEvents, pendingGoalSortIndex]
  );

  React.useEffect(() => {
    if (pendingGoalSortIndex !== null && !pendingGoal) {
      setPendingGoalSortIndex(null);
    }
  }, [pendingGoal, pendingGoalSortIndex]);

  const sortedMatchEvents = React.useMemo(
    () => sortMatchEvents(matchEvents),
    [matchEvents]
  );

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const persistTeams = async (emailTeams) => {
    try {
      if (typeof runAuthedRequest !== 'function') return;

      await runAuthedRequest(
        (idToken) =>
          axios.patch(
            `${process.env.REACT_APP_API_URL}/games/${game._id}/teams`,
            { teams: emailTeams },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure,
        }
      );
    } catch (err) {
      console.error('Failed to save new teams', err);
    }
  };

  const handleDragEnd = (result) => {
    if (isHalftime) return;

    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (!game?.teams || !Array.isArray(game.teams)) return;

    const fromTeam = Number(source.droppableId);
    const toTeam = Number(destination.droppableId);
    const fromIndex = source.index;
    const toIndex = clampDropIndex(game.teams?.[toTeam], destination.index);

    const updatedTeams = game.teams.map((team) => team.slice());
    const movingPlayer = updatedTeams[fromTeam]?.[fromIndex] || null;
    const targetPlayer = updatedTeams[toTeam]?.[toIndex] || null;

    updatedTeams[toTeam][toIndex] = movingPlayer;
    updatedTeams[fromTeam][fromIndex] = targetPlayer || null;

    dispatch(movePlayer({ fromTeam, fromIndex, toTeam, toIndex }));

    const emailTeams = updatedTeams.map((team) =>
      team.map((p) => (p?.email ? p.email : null))
    );
    persistTeams(emailTeams);

    if (fromTeam !== toTeam && movingPlayer?.email) {
      const switchEvents = createTeamSwitchEvents({
        matchEvents,
        durationMinutes: game?.duration,
        timerSeconds: timer,
        halftimeCompleted,
        switches: [
          {
            player: movingPlayer,
            fromTeamIndex: fromTeam,
            toTeamIndex: toTeam,
          },
          ...(targetPlayer?.email
            ? [
                {
                  player: targetPlayer,
                  fromTeamIndex: toTeam,
                  toTeamIndex: fromTeam,
                },
              ]
            : []),
        ],
      });

      if (switchEvents.length > 0) {
        dispatch(setMatchEvents([...matchEvents, ...switchEvents]));
      }
    }
  };

  const handleGoal = (player, teamIndex) => {
    if (isHalftime) return;

    const email = player?.email;
    if (!email) return;

    if (isCorrectMode) {
      const { removedEvent, matchEvents: nextEvents } = removeLastGoalEvent(
        matchEvents,
        email,
        teamIndex
      );

      if (!removedEvent) return;

      dispatch(updateGoalDelta({ key: email, teamIndex, delta: -1 }));
      if (removedEvent.assistEmail) {
        dispatch(updateAssistDelta({ key: removedEvent.assistEmail, delta: -1 }));
      }
      dispatch(setMatchEvents(nextEvents));
      if (removedEvent.sortIndex === pendingGoalSortIndex) {
        setPendingGoalSortIndex(null);
      }
      return;
    }

    dispatch(updateGoalDelta({ key: email, teamIndex, delta: 1 }));
    const goalEvent = createGoalEvent({
      matchEvents,
      player,
      teamIndex,
      durationMinutes: game?.duration,
      timerSeconds: timer,
      halftimeCompleted,
    });
    dispatch(setMatchEvents([...matchEvents, goalEvent]));
    setPendingGoalSortIndex(goalEvent.sortIndex);
  };

  const handleAssist = async (player, teamIndex) => {
    if (isHalftime) return;

    const email = player?.email;
    if (!email) return;

    if (isCorrectMode) {
      const { removedEvent, matchEvents: nextEvents } = removeAssistFromLatestGoal(
        matchEvents,
        email,
        teamIndex
      );

      if (!removedEvent) return;

      dispatch(updateAssistDelta({ key: email, delta: -1 }));
      dispatch(setMatchEvents(nextEvents));
      return;
    }

    if (!pendingGoal) {
      await showAlert({
        title: 'MATCH',
        message: 'Add the goal first, then select the assister.',
      });
      return;
    }

    if (pendingGoal.teamIndex !== teamIndex) {
      await showAlert({
        title: 'MATCH',
        message: 'The assister must come from the same team as the scorer.',
      });
      return;
    }

    if (pendingGoal.playerEmail === email) {
      await showAlert({
        title: 'MATCH',
        message: 'The scorer cannot also be the assister.',
      });
      return;
    }

    const { attached, matchEvents: nextEvents } = attachAssistToGoal(
      matchEvents,
      pendingGoal.sortIndex,
      player
    );

    if (!attached) return;

    dispatch(updateAssistDelta({ key: email, delta: 1 }));
    dispatch(setMatchEvents(nextEvents));
    setPendingGoalSortIndex(null);
  };

  const handleCard = (player, teamIndex, cardStatus) => {
    if (isHalftime) return;

    const email = player?.email;
    if (!email) return;

    if (isCorrectMode) {
      const removableType =
        cardStatus === 'red' ? 'red' : cardStatus === 'yellow' ? 'yellow' : null;
      if (!removableType) return;

      const { removedEvent, matchEvents: nextEvents } = removeLastCardEvent(
        matchEvents,
        email,
        removableType,
        teamIndex
      );

      if (!removedEvent) return;

      dispatch(updateCardCorrect({ key: email }));
      dispatch(setMatchEvents(nextEvents));
      return;
    }

    const nextCardType =
      cardStatus === 'none' ? 'yellow' : cardStatus === 'yellow' ? 'red' : null;

    dispatch(updateCard({ key: email }));

    if (!nextCardType) return;

    const cardEvent = createCardEvent({
      matchEvents,
      player,
      teamIndex,
      cardType: nextCardType,
      durationMinutes: game?.duration,
      timerSeconds: timer,
      halftimeCompleted,
    });
    dispatch(setMatchEvents([...matchEvents, cardEvent]));
  };

  const markLate = (email) => {
    if (isHalftime) return;

    if (!email) return;
    dispatch(updateAttendance({ key: email, status: 'late' }));
  };

  const handleSkipAssist = () => {
    setPendingGoalSortIndex(null);
  };

  const finishGame = async () => {
    if (isSubmittingFinish) return;

    const payload = buildFinishGamePayload({
      game,
      goals,
      assists,
      attendance,
      cardCounts,
      matchEvents,
      score,
    });

    try {
      if (typeof runAuthedRequest !== 'function') return;
      setIsSubmittingFinish(true);

      const response = await runAuthedRequest(
        (idToken) =>
          axios.put(
            `${process.env.REACT_APP_API_URL}/games/${game._id}/status`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure,
        }
      );
      if (!response) return;

      await showAlert({
        title: 'MATCH',
        message: 'Game finished and stats updated!',
      });
      onFinish();
    } catch (error) {
      const message =
        error?.response?.data?.error || 'Failed to finish the game. Please try again.';
      await showAlert({
        title: 'MATCH',
        message,
      });
      console.error('Error finishing game:', error);
    } finally {
      setIsSubmittingFinish(false);
    }
  };

  if (!game?.teams) return null;

  const handlePrimaryAction = () => {
    if (isSubmittingFinish) return;

    if (isHalftime) {
      dispatch(endHalftime());
      return;
    }

    if (!halftimeCompleted) {
      dispatch(startHalftime());
      return;
    }

    finishGame();
  };

  return (
    <div className="gs-container">
      {isSubmittingFinish && <LoadingOverlay label="Finishing game" />}
      <div className="gs-header">
        <div className="gs-timerWrap">
          {isHalftime ? <div className="gs-timerLabel">HALF TIME</div> : null}
          <div className="gs-timer">{isHalftime ? formatTime(timer) : `⏱ ${formatTime(timer)}`}</div>
        </div>
        <div className="gs-scoreboard">
          {score[0]} : {score[1]}
        </div>
        <Button
          text={actionButtonText}
          onClick={handlePrimaryAction}
          disabled={isSubmittingFinish}
        />
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="gs-lists">
          <div className="gs-modeToggle">
            <span className="gs-modeLabel">
              {isCorrectMode ? 'CORRECT' : 'ADD'}
            </span>
            <button
              type="button"
              className={`gs-switch ${isCorrectMode ? 'gs-switch--on' : ''}`}
              onClick={() => setIsCorrectMode((prev) => !prev)}
              aria-pressed={isCorrectMode}
              aria-label="Toggle correct mode"
            >
              <span className="gs-switchKnob" />
            </button>
          </div>

          {pendingGoal ? (
            <div className="gs-pendingAssist">
              <span className="gs-pendingAssistText">
                Goal by {pendingGoal.playerName || pendingGoal.playerEmail} at{' '}
                {pendingGoal.minute}'. Select the assister or skip it.
              </span>
              <button
                type="button"
                className="gs-pendingAssistSkip"
                onClick={handleSkipAssist}
              >
                No Assist
              </button>
            </div>
          ) : null}

          {game.teams.map((team, teamIndex) => (
            <Droppable
              droppableId={String(teamIndex)}
              key={teamIndex}
              direction="vertical"
            >
              {(provided) => (
                <div
                  className="gs-team"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <h2>Team {teamIndex + 1}</h2>

                  {team.map((player, playerIndex) => {
                    const draggableId = player?.email
                      ? `player:${player.email}`
                      : `empty:${teamIndex}:${playerIndex}`;

                    const email = player?.email || null;
                    const att = email ? attendance[email] : null;
                    const cardStatus = email ? cards?.[email] || 'none' : 'none';
                    const redUntil = email ? redCardUntil?.[email] || null : null;
                    const redRemainingSec =
                      cardStatus === 'red' && redUntil
                        ? Math.max(0, Math.ceil((redUntil - Date.now()) / 1000))
                        : 0;
                    const redMins = String(Math.floor(redRemainingSec / 60)).padStart(
                      2,
                      '0'
                    );
                    const redSecs = String(redRemainingSec % 60).padStart(2, '0');

                    const isAbsent = att === 'absent';

                    return (
                      <Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={playerIndex}
                        isDragDisabled={!player || isHalftime}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className={`gs-row ${
                              snapshot.isDragging ? 'gs-row--dragging' : ''
                            }`}
                          >
                            <span className="gs-rank">{playerIndex + 1}</span>

                            {!player ? (
                              <span className="gs-empty">Empty Slot</span>
                            ) : (
                              <>
                                <div className="gs-core">
                                  <img
                                    src={player.profilePicture || profilePic}
                                    alt="Player"
                                  />
                                  <div className="gs-info">
                                    <span className="gs-name">
                                      {player.first_name} {player.last_name}
                                    </span>

                                    {/* Phase 3 rule:
                                        - If absent => stats hidden
                                        - If late/present => show goals/assists
                                    */}
                                    <div className="gs-statsRow">
                                      {isAbsent ? null : (
                                        <div className="gs-actions">
                                          {(() => {
                                            const goalCount = goals[email] || 0;
                                            const assistCount = assists[email] || 0;
                                            const goalLabel =
                                              goalCount === 1 ? 'GOAL' : 'GOALS';
                                            const assistLabel =
                                              assistCount === 1
                                                ? 'ASSIST'
                                                : 'ASSISTS';
                                            const goalText = goalCount
                                              ? `${goalCount} ${goalLabel}`
                                              : goalLabel;
                                            const assistText = assistCount
                                              ? `${assistCount} ${assistLabel}`
                                              : assistLabel;

                                            return (
                                              <>
                                                <button
                                                  className="gs-statBtn"
                                                  onClick={() =>
                                                    handleGoal(player, teamIndex)
                                                  }
                                                  type="button"
                                                  aria-label="Add goal"
                                                  disabled={isHalftime}
                                                >
                                                  {goalText}
                                                </button>

                                                <button
                                                  className="gs-statBtn"
                                                  onClick={() =>
                                                    handleAssist(player, teamIndex)
                                                  }
                                                  type="button"
                                                  aria-label="Add assist"
                                                  disabled={isHalftime}
                                                >
                                                  {assistText}
                                                </button>
                                              </>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="gs-cardWrap">
                                  {isAbsent ? (
                                    <button
                                      type="button"
                                      className="gs-lateBtn"
                                      onClick={() => markLate(email)}
                                      title="Mark as late"
                                      aria-label="Mark as late"
                                      disabled={isHalftime}
                                    >
                                      <img
                                        src={LATE_ICON.inactive}
                                        alt="late"
                                        className="gs-lateImg"
                                        draggable="false"
                                      />
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="gs-cardBtn"
                                        onClick={() => handleCard(player, teamIndex, cardStatus)}
                                        aria-label="Cycle card"
                                        disabled={isHalftime}
                                      >
                                        <img
                                          src={CARD_ICON[cardStatus]}
                                          alt={cardStatus}
                                          className="gs-cardImg"
                                          draggable="false"
                                        />
                                      </button>
                                      {cardStatus === 'red' &&
                                      redRemainingSec > 0 ? (
                                        <span className="gs-cardTimer">
                                          {redMins}:{redSecs}
                                        </span>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>

        <div className="gs-eventsPanel">
          <div className="gs-eventsHeader">Match Events</div>
          {sortedMatchEvents.length > 0 ? (
            <ul className="gs-eventsList">
              {[...sortedMatchEvents].reverse().map((event) => (
                <li
                  key={`${event.sortIndex}-${event.type}-${event.playerEmail || event.playerName}`}
                  className={`gs-eventItem gs-eventItem--${
                    event.teamIndex === 1 ? 'team2' : 'team1'
                  }`}
                >
                  <span className="gs-eventMinute">{event.minute}'</span>
                  <span className="gs-eventText">{describeMatchEvent(event)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="gs-eventsEmpty">No match events recorded yet.</div>
          )}
        </div>
      </DragDropContext>
    </div>
  );
};

export default GameStats;
